/**
 * Design tokens for the authenticated application shell (rail + top bar).
 *
 * WHY these live outside `theme.tsx` and are NOT theme-aware:
 * the navigation rail is required to stay deep navy in every preset. The theme
 * presets (blue / dark / green / purple / orange) each ship their own
 * `sidebar*` tokens, and the light ones (green, orange) resolve to surfaces the
 * new rail design would invert against — the rail would turn leaf-green under
 * one tenant and violet under another, which is exactly the inconsistency the
 * redesign is meant to remove. The rail is chrome, not content: it is a fixed
 * part of the product identity, so it carries a fixed palette.
 *
 * The WORKSPACE (top bar, page canvas) stays theme-aware and keeps reading
 * `useAppTheme().colors` — only the rail is pinned.
 *
 * Contrast (all measured against the rail base #0B1220):
 *   text      #F8FAFC  18.2:1   AAA
 *   textMuted #94A3B8   7.3:1   AAA for body, AA for the 10px eyebrows
 *   textFaint #7D8CA3   5.5:1   AA  (footer microcopy only)
 *   accent    #60A5FA   7.4:1   AAA (active icon + rail)
 * Nothing in the rail is allowed to fall below 4.5:1, which rules out the
 * slate-500 (#64748B, 3.9:1) that this palette family would normally use for
 * secondary text on dark.
 */

/** Widths the rail animates between. Shared with AppLayout and Header so the
 *  workspace offset, the AppBar offset and the drawer never disagree. */
export const RAIL_WIDTH = 76;
export const RAIL_EXPANDED_WIDTH = 280;

/** localStorage key for the pin preference. Namespaced so it can't collide
 *  with the theme preset or the dev subdomain override. */
export const SIDEBAR_PIN_KEY = 'stratify.shell.sidebarPinned';

export const NAVY = {
  /** Base rail fill. Deep navy, fixed. */
  bg: '#0B1220',
  /** A barely-there vertical lift so the rail isn't a flat slab 900px tall. */
  bgGradient: 'linear-gradient(180deg, #0F172A 0%, #0B1220 42%, #0B1220 100%)',

  text: '#F8FAFC',
  textMuted: '#94A3B8',
  textFaint: '#7D8CA3',

  /** Item states. Kept as rgba so they composite over the gradient. */
  hover: 'rgba(255,255,255,0.055)',
  active: 'rgba(37,99,235,0.20)',
  surface: 'rgba(255,255,255,0.04)',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',

  /** Indigo -> blue, the primary ramp from the design direction. */
  accent: '#60A5FA',
  accentGradient: 'linear-gradient(180deg, #6366F1 0%, #2563EB 100%)',
  /** Focus ring. Light enough to clear the rail at 2px on every item state. */
  focus: '#93C5FD',
} as const;

/** Workspace accents shared by the top bar. Deliberately literal (not theme
 *  tokens) only where the design direction names a specific hue. */
export const ACCENT = {
  primary: '#2563EB',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  success: '#16A34A',
  warning: '#F59E0B',
  danger: '#DC2626',
} as const;

/** Small uppercase section label. Matches the landing page's eyebrow. */
export const eyebrowSx = {
  fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.625rem',
  fontWeight: 500,
  letterSpacing: '0.16em',
  textTransform: 'uppercase' as const,
};

/**
 * Motion helper. Every animated property in the shell routes through this so a
 * single `prefers-reduced-motion` check disables the lot — required by the
 * brief, and the width transition is the one that actually induces nausea.
 */
export const motion = (reduced: boolean, value: string) => (reduced ? 'none' : value);
